/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

/**
 * useTerritories.js  (admin dashboard)
 * ------------------------------------
 * Markets list, read from the same Territory collection the employee portal uses.
 * This is what makes agent assignment (agentArea) and demo territories agree —
 * they read the same rows.
 *
 * Markets are ADDED and EDITED in the employee portal's Control Center → Markets
 * tab. This app only reads them, so there's no service layer here — just the one
 * GET, which is public and needs no auth header.
 *
 *   const { names, tzFor, stateFor, loading, error } = useTerritories();
 *
 * No fallback list on purpose: a stale hardcoded fallback is exactly how the old
 * territory arrays drifted apart across files. On error, disable the control.
 */

import { useState, useEffect, useCallback } from 'react';

// Same base the rest of this app uses (see CreateAgentForm).
const API_URL = 'https://back-end-x7tx.onrender.com/api';

let _cache = null;
let _inflight = null;

async function fetchOnce(force = false) {
  if (force) {
    _cache = null;
    _inflight = null;
  }
  if (_cache) return _cache;
  if (_inflight) return _inflight;

  _inflight = fetch(`${API_URL}/territories`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
    .then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Failed to fetch markets');
      _cache = data.territories || [];
      _inflight = null;
      return _cache;
    })
    .catch((err) => {
      _inflight = null;
      throw err;
    });

  return _inflight;
}

export function invalidateTerritoryCache() {
  _cache = null;
  _inflight = null;
}

export default function useTerritories() {
  const [territories, setTerritories] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState('');

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError('');
    try {
      setTerritories(await fetchOnce(force));
    } catch (err) {
      setError(err.message || 'Could not load markets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!_cache) load();
  }, [load]);

  const names = territories.map((t) => t.name);

  const tzFor = useCallback(
    (name) => {
      const t = territories.find((x) => x.name === name);
      return t ? t.timezone : null;
    },
    [territories]
  );

  const stateFor = useCallback(
    (name) => {
      const t = territories.find((x) => x.name === name);
      return t && t.state ? t.state : null;
    },
    [territories]
  );

  return { territories, names, tzFor, stateFor, loading, error, reload: () => load(true) };
}